import { Injectable } from "@angular/core";
import { Workbook } from "exceljs";
import * as fs from "file-saver";

@Injectable({
  providedIn: "root",
})
export class ExcelReportService {
  /// --------------------------------------------------------------------
  /// ExcelReportService uses exceljs to build a report's spreadsheet file
  /// --------------------------------------------------------------------
  exportExcel(excelData) {
    //Create workbook and worksheet with title row; show gridlines and freeze the first two rows
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(excelData.title);
    worksheet.views = [
      { showGridLines: false },
      { state: "frozen", xSplit: 0, ySplit: 2 },
    ];

    worksheet.addRow([excelData.titleWithDate]);
    worksheet.mergeCells(1, 1, 1, excelData.columns.length);
    let titleRow = worksheet.getRow(1);
    titleRow.height = 24;
    titleRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "153D6C" },
    };
    titleRow.getCell(1).font = {
      name: "Arial",
      size: 12,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    titleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };

    // Add table with striped rows
    let reportTable = worksheet.addTable({
      name: "Report",
      ref: "A2",
      headerRow: true,
      totalsRow: false,
      style: {
        theme: "TableStyleMedium2",
        showRowStripes: true,
        showColumnStripes: false,
      },
      columns: excelData.columns,
      rows: excelData.data,
    });

    // Set font and alignment for each row in the table; add thin borders
    for (let i = 2; i <= worksheet.actualRowCount; i++) {
      let row = worksheet.getRow(i);
      if (i === 2) {
        // header row
        row.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "FFFFFF" },
        };
        row.alignment = {
          vertical: "top",
          horizontal: "center",
          wrapText: true,
        };
      } else {
        // all data rows
        row.font = { name: "Arial", size: 9, color: { argb: "00000000" } };
        row.alignment = { vertical: "top", horizontal: "left", wrapText: true };
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { bottom: { style: "thin" }, right: { style: "thin" } };
        });
      }
    }

    // Set width and number format for each column
    for (let i = 0; i < worksheet.actualColumnCount; i++) {
      let column = worksheet.getColumn(i + 1);
      column.width = 20;
      column.numFmt = excelData.formats[i];
    }

    // Write the workbook
    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveAsExcelFile(buffer, excelData.title);
    });
  }

  exportExcelMultiReport(excelData: any) {
    //Create workbook and worksheet with title row; show gridlines and freeze the first two rows
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(excelData.title);
    worksheet.views = [
      { showGridLines: false },
      { state: "frozen", xSplit: 0, ySplit: 8 },
    ];

    worksheet.addRow([excelData.titleWithDate]);
    worksheet.mergeCells(1, 1, 1, excelData.columns.length);
    let titleRow = worksheet.getRow(1);
    titleRow.height = 24;
    titleRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "153D6C" },
    };
    titleRow.getCell(1).font = {
      name: "Arial",
      size: 12,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    titleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };

    worksheet.addRow(["Report Criteria"]);
    worksheet.mergeCells(2, 1, 2, excelData.columns.length);
    let criteriaHeaderRow = worksheet.getRow(2);
    criteriaHeaderRow.height = 15;
    criteriaHeaderRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "153D6C" },
    };
    criteriaHeaderRow.getCell(1).font = {
      name: "Arial",
      size: 11,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    criteriaHeaderRow.getCell(1).alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    worksheet.addRow(["Filter:", excelData.filters]);
    worksheet.mergeCells(3, 2, 3, excelData.columns.length);
    let filterHeaderRow = worksheet.getRow(3);
    filterHeaderRow.getCell(1).font = {
      name: "Arial",
      size: 10,
      bold: true,
      color: { argb: "00000000" },
    };
    filterHeaderRow.getCell(1).alignment = {
      vertical: "middle",
      horizontal: "right",
    };
    filterHeaderRow.getCell(2).alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };

    let noteLength = excelData.filters.length; // setting the column widths for the entire worksheet, below, to 22 seems to result
    const maxLineChars = (excelData.columns.length - 1) * 24; // in approximately 24 Arial 10pt characters fitting nicely per column
    let lineCount = Math.ceil(noteLength / maxLineChars);
    filterHeaderRow.height = 15 * lineCount;

    worksheet.addRow(["Deployability:", excelData.deployability]);
    worksheet.mergeCells(4, 2, 4, excelData.columns.length);
    let deployabilityHeaderRow = worksheet.getRow(4);
    deployabilityHeaderRow.height = 12;
    deployabilityHeaderRow.getCell(1).font = {
      name: "Arial",
      size: 10,
      bold: true,
      color: { argb: "00000000" },
    };
    deployabilityHeaderRow.getCell(1).alignment = {
      vertical: "middle",
      horizontal: "right",
    };

    worksheet.addRow([""]);
    worksheet.addRow([""]);

    worksheet.addRow(["Report Details"]);
    worksheet.mergeCells(7, 1, 7, excelData.columns.length);
    let detailsHeaderRow = worksheet.getRow(7);
    detailsHeaderRow.height = 15;
    detailsHeaderRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "153D6C" },
    };
    detailsHeaderRow.getCell(1).font = {
      name: "Arial",
      size: 11,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    detailsHeaderRow.getCell(1).alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    // Add table with striped rows
    let reportTable = worksheet.addTable({
      name: "Report",
      ref: "A8",
      headerRow: true,
      totalsRow: false,
      style: {
        theme: "TableStyleMedium2",
        showRowStripes: true,
        showColumnStripes: false,
      },
      columns: excelData.columns,
      rows: excelData.data,
    });

    // Set font and alignment for each row in the table; add thin borders
    for (let i = 8; i <= worksheet.actualRowCount + 2; i++) {
      let row = worksheet.getRow(i);
      if (i === 8) {
        // header row
        row.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "FFFFFF" },
        };
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "538DD5" },
          };
        });
        row.alignment = {
          vertical: "top",
          horizontal: "center",
          wrapText: false,
        };
      } else {
        // all data rows
        row.font = { name: "Arial", size: 9, color: { argb: "00000000" } };
        row.alignment = { vertical: "top", horizontal: "left", wrapText: true };
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { bottom: { style: "thin" }, right: { style: "thin" } };
        });
      }
    }

    // Set width and number format for each column
    for (let i = 0; i < worksheet.actualColumnCount; i++) {
      let column = worksheet.getColumn(i + 1);
      column.width = 22;
      column.numFmt = excelData.formats[i];
    }

    // Write the workbook
    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveAsExcelFile(buffer, excelData.title);
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const EXCEL_EXTENSION = ".xlsx";
    const data = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    fs.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }
}
